/*
 * cart-volume-pricing.js
 *
 * Bundler volume discounts are real at checkout but are NOT exposed in Shopify's
 * Liquid cart object, so the cart renders full prices (item.final_price /
 * item.final_line_price never reflect the tier). This mirrors what the PDP and
 * the product cards already do: read the offers from window.bndlr.getBundles(),
 * find the tier that applies at each line's quantity, and rewrite the per-stuk
 * and line prices client-side so the cart matches the PDP and the real checkout.
 *
 * Scope: per-stuk + line totals, plus the cart subtotal (the total Bundler
 * saving is subtracted from cart.total_price, leaving any other discounts
 * intact). Covers the cart page (.cart-item__unit-prices, .cart-item__line-price,
 * .cart-summary__subtotal + sticky checkout) and the drawer
 * (.cart-item__price-wrapper, .totals__total-value).
 *
 * Offer parsing + tier math come from bundler-volume-core.js (window.BundlerVolume),
 * shared with card-volume-tooltip.js so the rounding stays identical everywhere.
 */
(function () {
  'use strict';

  var DEBUG = false;
  var BV = window.BundlerVolume;

  /* ---------- VAT-aware money rendering (mirrors snippets/vat-money.liquid) ---------- */

  function vatRate() {
    return (window.RotimVat && window.RotimVat.VAT_RATE) || 21;
  }

  function inclCents(netCents) {
    return Math.round(netCents * (100 + vatRate()) / 100);
  }

  // Prefer the theme's shared formatter (quote-cart.js) so output matches the
  // Liquid `| money` filter; fall back to plain Dutch formatting until it loads.
  function money(cents) {
    if (typeof window.formatMoney === 'function') {
      try { return window.formatMoney(cents); } catch (e) { /* fall through */ }
    }
    return '€' + (cents / 100).toFixed(2).replace('.', ',');
  }

  // Reproduce vat-money's dual-span markup: exactly one span is shown, toggled
  // by the `prices-incl-vat` class on <html> (vat-switcher.css), so the header
  // VAT switch keeps working without re-rendering.
  function priceSpans(netCents, suffix) {
    suffix = suffix || '';
    return '<span class="vat-amount vat-amount--excl">' + money(netCents) + suffix + '</span>' +
      '<span class="vat-amount vat-amount--incl">' + money(inclCents(netCents)) + suffix + '</span>';
  }

  /* ---------- per-line rendering ---------- */

  function renderDiscount(line, baseNet, perUnit, qty) {
    var div = parseInt(line.getAttribute('data-bndlr-per-stuk-div'), 10);
    if (!Number.isFinite(div) || div <= 0) div = 0;

    // Cart page: per-stuk block → struck original + discounted "/st".
    var unitPrices = line.querySelector('.cart-item__unit-prices');
    if (unitPrices) {
      var origPer = div > 0 ? Math.ceil(baseNet / div) : baseNet;
      var newPer = div > 0 ? Math.ceil(perUnit / div) : perUnit;
      unitPrices.innerHTML =
        '<s class="cart-item__unit-old">' + priceSpans(origPer, '') + '</s>' +
        '<span class="cart-item__unit-new">' + priceSpans(newPer, ' ' + (((window.volumePricingStrings || {}).perUnitSuffix) || '/st')) + '</span>';
    }

    // Cart page: line total → discounted (qty × discounted unit).
    var linePrice = line.querySelector('.cart-item__line-price');
    if (linePrice) {
      linePrice.innerHTML = priceSpans(perUnit * qty, '');
    }

    // Drawer: line total → struck original line + discounted line, preserving
    // any sibling unit-price node in the wrapper.
    var wrapper = line.querySelector('.cart-item__price-wrapper');
    if (wrapper) {
      var mainPrice = wrapper.querySelector(':scope > .cart-item__discounted-prices, :scope > .price');
      if (mainPrice) {
        var holder = document.createElement('div');
        holder.className = 'cart-item__discounted-prices';
        holder.innerHTML =
          '<s class="cart-item__old-price price price--end">' + priceSpans(baseNet * qty, '') + '</s>' +
          '<span class="price price--end">' + priceSpans(perUnit * qty, '') + '</span>';
        mainPrice.replaceWith(holder);
      }
    }
  }

  // Rewrites the line's prices and returns its Bundler saving in net cents (0
  // when no tier applies), so the caller can adjust the subtotal.
  //
  // data-bndlr-base-net is the line's original_price, so paid add-ons (Optis
  // options like "Plaats van bedrukking") are included: Bundler discounts the
  // whole line at checkout. Lines flagged data-bndlr-applied already carry the
  // discount in Liquid (Bundler applied its code to the cart), so final_price
  // and cart.total_price are correct and subtracting again would double-count.
  function applyToLine(line, offers) {
    if (line.hasAttribute('data-bndlr-applied')) return 0;
    var productId = BV.normaliseId(line.getAttribute('data-bndlr-product-id'));
    var baseNet = parseInt(line.getAttribute('data-bndlr-base-net'), 10);
    if (!productId || !Number.isFinite(baseNet) || baseNet <= 0) return 0;

    var input = line.querySelector('.quantity__input');
    var qty = input ? parseInt(input.value, 10) : NaN;
    if (!Number.isFinite(qty) || qty < 1) qty = 1;

    // Overlapping offers are resolved by the shared core the same way Bundler's
    // checkout does; picking the first matching offer here showed full price
    // when a product-specific offer's tiers start above the line quantity.
    var match = BV.bestOfferTierForQty(offers, productId, qty);
    var tier = match ? match.tier : null;

    var perUnit = tier ? BV.computeTier(baseNet, tier).perUnitCents : baseNet;
    var hasSaving = tier && Number.isFinite(perUnit) && perUnit >= 0 && perUnit < baseNet;
    var savings = hasSaving ? (baseNet - perUnit) * qty : 0;

    // Signature so repeat passes (MutationObserver / VAT toggle) skip the DOM
    // write and never feed back into the observer. Includes a formatter-ready
    // bit so the line re-renders once window.formatMoney finishes loading.
    var fmtReady = typeof window.formatMoney === 'function';
    var tierKey = tier ? (tier.discountType + tier.minQty + ':' + tier.discountValue) : 'none';
    var sig = baseNet + '|' + qty + '|' + tierKey + '|' + (fmtReady ? 'f' : 'n');
    if (line.getAttribute('data-bndlr-sig') !== sig) {
      if (hasSaving) renderDiscount(line, baseNet, perUnit, qty);
      line.setAttribute('data-bndlr-sig', sig);
    }
    return savings;
  }

  /* ---------- subtotal ---------- */

  // Cart-page / sticky subtotal: <strong> excl + <strong> incl + "(incl … BTW)".
  function renderDetailedSubtotal(valueEl, net) {
    var inclNet = inclCents(net);
    var excl = valueEl.querySelector('.vat-amount--excl strong');
    var incl = valueEl.querySelector('.vat-amount--incl strong');
    var inclTax = valueEl.querySelector('.vat-amount--incl .tax');
    if (excl) excl.textContent = money(net);
    if (incl) incl.textContent = money(inclNet);
    if (inclTax) inclTax.textContent = '(incl ' + money(inclNet - net) + ' BTW)';
  }

  // Drawer subtotal: a plain vat-money dual-span block.
  function renderSimpleSubtotal(el, net) {
    el.innerHTML = priceSpans(net, '');
  }

  // The sticky checkout subtotal lives outside .js-contents, so cart.js never
  // re-renders it (and its cart total would go stale). Keep it a pure mirror of
  // the main subtotal, like the inline sync in main-cart-footer.liquid, but here
  // it also covers initial load. Idempotent: copies only when they differ.
  function mirrorSticky() {
    var source = document.querySelector('#main-cart-footer .cart-summary__subtotal .value');
    var target = document.querySelector('.cart-sticky-checkout__value');
    if (source && target && target.innerHTML !== source.innerHTML) {
      target.innerHTML = source.innerHTML;
    }
  }

  // Subtract the surface's Bundler saving from Shopify's cart.total_price (read
  // from data-bndlr-cart-total, re-rendered each cart update) so other discounts
  // are preserved. Skips when there is no saving (Liquid's value is correct) and
  // signature-guards the write so it never loops the observer.
  function updateSubtotals(selector, detailed, savings) {
    if (savings <= 0) return;
    document.querySelectorAll(selector).forEach(function (el) {
      var cartNet = parseInt(el.getAttribute('data-bndlr-cart-total'), 10);
      if (!Number.isFinite(cartNet)) return;
      var net = Math.max(0, cartNet - savings);
      var fmtReady = typeof window.formatMoney === 'function';
      var sig = net + '|' + (fmtReady ? 'f' : 'n');
      if (el.getAttribute('data-bndlr-sub-sig') === sig) return;
      if (detailed) renderDetailedSubtotal(el, net);
      else renderSimpleSubtotal(el, net);
      el.setAttribute('data-bndlr-sub-sig', sig);
    });
  }

  // Free shipping bar: Liquid renders it from the raw cart.total_price, but
  // Shopify's price-based shipping rate is evaluated on the post-discount
  // subtotal at checkout, so a Bundler saving can drop the cart back under the
  // threshold while the bar still promises free shipping. Recompute it from the
  // same net total the subtotal uses. Mirrors renderShippingBar() in
  // quote-cart.js so both bars round and format identically. No VAT conversion:
  // the threshold and cart.total_price are both net.
  function updateShippingBar(selector, savings) {
    if (savings <= 0) return;
    var bar = document.querySelector(selector);
    if (!bar) return;

    var threshold = parseInt(bar.getAttribute('data-fs-threshold'), 10);
    var cartNet = parseInt(bar.getAttribute('data-bndlr-cart-total'), 10);
    if (!Number.isFinite(threshold) || threshold <= 0) return;
    if (!Number.isFinite(cartNet)) return;

    var net = Math.max(0, cartNet - savings);
    var fmtReady = typeof window.formatMoney === 'function';
    var sig = net + '|' + (fmtReady ? 'f' : 'n');
    if (bar.getAttribute('data-bndlr-fs-sig') === sig) return;

    var remaining = threshold - net;
    var textEl = bar.querySelector('.cart-shipping-bar__text');
    var fillEl = bar.querySelector('.cart-shipping-bar__fill');

    if (textEl) {
      // Function replacement: money() can contain a `$` from shop.money_format,
      // which a string replacement would read as a substitution pattern.
      textEl.innerHTML = remaining > 0
        ? (bar.getAttribute('data-fs-remaining-html') || '').replace('[amount]', function () { return money(remaining); })
        : (bar.getAttribute('data-fs-qualified-html') || '');
    }
    if (fillEl) {
      fillEl.style.width = (remaining > 0 ? Math.min(100, net * 100 / threshold) : 100) + '%';
    }

    bar.setAttribute('data-bndlr-fs-sig', sig);
  }

  /* ---------- orchestration ---------- */

  function ready() {
    return window.bndlr && typeof window.bndlr.getBundles === 'function';
  }

  function getOffers() {
    try {
      var offers = BV.getVolumeOffers(window.bndlr.getBundles());
      if (DEBUG) console.log('[cart-volume-pricing] volume offers:', offers.length, offers);
      return offers;
    } catch (e) {
      if (DEBUG) console.warn('[cart-volume-pricing] getBundles() threw:', e);
      return [];
    }
  }

  // Each cart surface: its line container + the subtotal(s) to adjust. Savings
  // are summed PER container because on the cart page the drawer is also in the
  // DOM — a global sum would double-count its (duplicate) lines.
  var SURFACES = [
    {
      items: '#main-cart-items',
      subtotal: '#main-cart-footer .cart-summary__subtotal .value',
      shippingBar: '#main-cart-footer .cart-shipping-bar',
      detailed: true
    },
    {
      items: '#CartDrawer-CartItems',
      subtotal: '.cart-drawer__footer .totals__total-value',
      detailed: false
    }
  ];

  // Re-entrancy guard: renderDiscount mutates the cart subtree the observer
  // watches; the flag (reset next frame) keeps that from looping.
  var applying = false;
  function apply() {
    if (applying || !BV || !ready()) return;
    if (!document.querySelector('.cart-item[data-bndlr-product-id]')) return;
    var offers = getOffers();
    if (!offers.length) return;
    applying = true;
    try {
      SURFACES.forEach(function (surface) {
        var root = document.querySelector(surface.items);
        if (!root) return;
        var savings = 0;
        root.querySelectorAll('.cart-item[data-bndlr-product-id]').forEach(function (line) {
          savings += applyToLine(line, offers);
        });
        updateSubtotals(surface.subtotal, surface.detailed, savings);
        if (surface.shippingBar) updateShippingBar(surface.shippingBar, savings);
      });
      mirrorSticky();
    } finally {
      requestAnimationFrame(function () { applying = false; });
    }
  }

  function init() {
    // Re-run when Bundler signals (re)load.
    document.addEventListener('bundler:loaded', apply);
    document.addEventListener('bundler:rendered', apply);

    // The MutationObserver below is debounced 150ms, long enough to see the
    // un-discounted Liquid render flash back after a quantity change, which is
    // jarring on the free shipping bar. cart.js publishes cartUpdate right after
    // the section swap, so hook that too and correct in the same tick.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, apply);
    }

    apply();

    // Bounded fallback until window.bndlr + window.formatMoney finish loading.
    var tries = 0;
    var interval = setInterval(function () {
      tries++;
      apply();
      if (tries > 40 || (ready() && typeof window.formatMoney === 'function')) clearInterval(interval);
    }, 300);

    // Cart updates (qty change, drawer open) replace .js-contents innerHTML,
    // wiping our edits, so re-apply on DOM changes. Debounced; the `applying`
    // guard + per-line signature keep our own writes from looping.
    if ('MutationObserver' in window) {
      var pending = null;
      var observer = new MutationObserver(function () {
        if (pending) return;
        pending = setTimeout(function () { pending = null; apply(); }, 150);
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
